import type { ReactNode } from "react";
import { cn } from "@base/ui";
import { BadgeCheck, Plug } from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";

/** Situação de uma rede, na cor que a pílula mostra. */
export type TomDaRede = "conectada" | "atencao" | "erro" | "desligada";

const ESTILO_DO_TOM: Record<TomDaRede, { pilula: string; ponto: string }> = {
  conectada: {
    pilula: "border-success-line bg-success-soft text-success-soft-foreground",
    ponto: "bg-success",
  },
  atencao: {
    pilula: "border-warning-line bg-warning/10 text-foreground",
    ponto: "bg-warning",
  },
  erro: {
    pilula: "border-destructive/30 bg-destructive/10 text-destructive",
    ponto: "bg-destructive",
  },
  desligada: {
    pilula: "border-border bg-muted/40 text-muted-foreground",
    ponto: "bg-muted-foreground/40",
  },
};

export function PilulaDeStatus({
  tom,
  rotulo,
}: {
  readonly tom: TomDaRede;
  readonly rotulo: string;
}) {
  const estilo = ESTILO_DO_TOM[tom];
  return (
    <span
      className={cn(
        "inline-flex shrink-0 items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-medium",
        estilo.pilula,
      )}
    >
      <span className={cn("size-1.5 rounded-full", estilo.ponto)} aria-hidden="true" />
      {rotulo}
    </span>
  );
}

/** Título da seção com quantas redes já publicam. */
export function CabecalhoDasRedes({
  conectadas,
  total,
}: {
  readonly conectadas: number;
  readonly total: number;
}) {
  const fracao = total > 0 ? Math.min(1, conectadas / total) : 0;
  return (
    <div className="flex flex-wrap items-end justify-between gap-4">
      <div className="max-w-xl space-y-1.5">
        <h2 className="text-lg font-semibold tracking-tight text-foreground">
          Redes do projeto
        </h2>
        <p className="text-sm leading-relaxed text-muted-foreground">
          Conecte as contas do cliente para a agenda publicar. O Instagram de{" "}
          <span className="font-medium text-foreground">Configurações da conta</span>{" "}
          só acompanha referências e stories.
        </p>
      </div>
      <div
        className="flex items-center gap-3 rounded-full border border-border bg-background px-3.5 py-1.5"
        aria-label={`${conectadas} de ${total} redes conectadas`}
      >
        <span className="text-sm text-muted-foreground">
          <span className="font-semibold tabular-nums text-foreground">{conectadas}</span>
          <span className="tabular-nums">/{total}</span> conectadas
        </span>
        <span className="h-1.5 w-16 overflow-hidden rounded-full bg-muted" aria-hidden="true">
          <span
            className="block h-full rounded-full bg-success transition-[width] duration-500 motion-reduce:transition-none"
            style={{ width: `${fracao * 100}%` }}
          />
        </span>
      </div>
    </div>
  );
}

/** Cartão de uma rede: marca, situação, contas e ações no rodapé. */
export function CartaoDeRede({
  logo,
  nome,
  descricao,
  tom,
  rotuloDoStatus,
  acoes,
  children,
  className,
}: {
  readonly logo: ReactNode;
  readonly nome: string;
  readonly descricao: string;
  readonly tom: TomDaRede;
  readonly rotuloDoStatus: string;
  readonly acoes?: ReactNode;
  readonly children?: ReactNode;
  readonly className?: string;
}) {
  return (
    <section
      className={cn(
        "flex flex-col rounded-2xl border border-border bg-card transition-colors hover:border-foreground/20",
        className,
      )}
      aria-label={nome}
    >
      <header className="space-y-3 p-5">
        <div className="flex items-center gap-3">
          <span
            className="flex size-11 shrink-0 items-center justify-center gap-1 rounded-xl border border-border bg-muted/50 text-foreground"
            aria-hidden="true"
          >
            {logo}
          </span>
          <h3 className="min-w-0 flex-1 text-base font-semibold tracking-tight text-foreground">
            {nome}
          </h3>
          <PilulaDeStatus tom={tom} rotulo={rotuloDoStatus} />
        </div>
        <p className="text-sm leading-relaxed text-muted-foreground">{descricao}</p>
      </header>
      {children ? <div className="flex-1 space-y-3 px-5 pb-5">{children}</div> : null}
      {acoes ? (
        <footer className="mt-auto flex flex-wrap items-center gap-2 rounded-b-2xl border-t border-border/60 bg-muted/20 px-5 py-3">
          {acoes}
        </footer>
      ) : null}
    </section>
  );
}

const FORMATO_COMPACTO = new Intl.NumberFormat("pt-BR", {
  notation: "compact",
  maximumFractionDigits: 1,
});

export type NumeroDoPerfil = { readonly rotulo: string; readonly valor: number | null };

/** Conta já ligada: foto com o selo da rede, nome, detalhe e números. */
export function ContaDaRede({
  fotoUrl,
  iniciais,
  selo,
  nome,
  detalhe,
  verificado = false,
  numeros = [],
}: {
  readonly fotoUrl: string | null;
  readonly iniciais: string;
  readonly selo: ReactNode;
  readonly nome: string;
  readonly detalhe: string;
  readonly verificado?: boolean;
  readonly numeros?: readonly NumeroDoPerfil[];
}) {
  const visiveis = numeros.filter((numero) => numero.valor !== null);
  return (
    <div className="rounded-xl border border-border/70 bg-background p-3">
      <div className="flex items-center gap-3">
        <span className="relative shrink-0">
          <Avatar className="size-11 overflow-hidden">
            {fotoUrl ? (
              <AvatarImage
                src={fotoUrl}
                alt=""
                referrerPolicy="no-referrer"
                className="h-full w-full object-cover"
              />
            ) : null}
            <AvatarFallback className="bg-muted text-xs font-medium text-foreground">
              {iniciais}
            </AvatarFallback>
          </Avatar>
          <span
            className="absolute -right-1 -bottom-1 flex size-5 items-center justify-center rounded-full border-2 border-background bg-foreground text-background [&_svg]:size-2.5"
            aria-hidden="true"
          >
            {selo}
          </span>
        </span>
        <div className="min-w-0 flex-1">
          <p className="flex items-center gap-1 text-sm font-medium leading-snug text-foreground">
            <span className="truncate">{nome}</span>
            {verificado ? (
              <BadgeCheck className="size-4 shrink-0 text-foreground" aria-label="Verificada" />
            ) : null}
          </p>
          <p className="truncate text-xs leading-relaxed text-muted-foreground">{detalhe}</p>
        </div>
      </div>
      {visiveis.length > 0 ? (
        <dl
          className="mt-3 grid gap-2"
          style={{ gridTemplateColumns: `repeat(${visiveis.length}, minmax(0, 1fr))` }}
        >
          {visiveis.map((numero) => (
            <div key={numero.rotulo} className="rounded-lg bg-muted/40 px-3 py-2">
              <dt className="text-xs text-muted-foreground">{numero.rotulo}</dt>
              <dd className="text-sm font-semibold tabular-nums text-foreground">
                {FORMATO_COMPACTO.format(numero.valor ?? 0)}
              </dd>
            </div>
          ))}
        </dl>
      ) : null}
    </div>
  );
}

/** Quando nada está ligado ainda: diz o que a conexão libera. */
export function RedeVazia({ children }: { readonly children: ReactNode }) {
  return (
    <div className="flex items-start gap-3 rounded-xl border border-dashed border-border px-4 py-4">
      <Plug className="mt-0.5 size-4 shrink-0 text-muted-foreground" aria-hidden="true" />
      <p className="text-sm leading-relaxed text-muted-foreground">{children}</p>
    </div>
  );
}

/** Recado curto dentro do cartão (expirando, falta escolher...). */
export function RecadoDaRede({
  tom = "neutro",
  children,
}: {
  readonly tom?: "neutro" | "atencao" | "erro";
  readonly children: ReactNode;
}) {
  return (
    <p
      className={cn(
        "rounded-lg px-3 py-2 text-sm leading-relaxed",
        tom === "erro" && "bg-destructive/10 text-destructive",
        tom === "atencao" && "bg-warning/10 text-foreground",
        tom === "neutro" && "bg-muted/40 text-muted-foreground",
      )}
      role="status"
    >
      {children}
    </p>
  );
}
